import AppStoreContext from 'contexts/AppStoreContext';
import { List, OrderedMap, OrderedSet } from 'immutable';
import * as React from 'react';
import { NewRFI } from 'records/NewRFI';
import { NewRFIForwardActivity } from 'records/NewRFIForwardActivity';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { RFI, StudioData } from 'records/RFI';
import { RFIAnswer } from 'records/RFIAnswer';
import { RFIAttachment } from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { StudioProjectFileData } from 'records/StudioProjectFile';

type RFIAttachments = {
  fileAttachments: List<RFIFile>;
  driveAttachments: List<ProjectDriveSelectedItem>;
  integrationAttachments: List<IntegrationsSelectedItem>;
  studioAttachments: List<StudioProjectFileData>;
  studioData: StudioData;
};

type CreateRFIForwardActivityInput = RFIAttachments & {
  rfi: RFI;
  projectid: string;
  forwardActivity: NewRFIForwardActivity;
};

type CreateOfficialResponseInput = RFIAttachments & {
  rfi: RFI;
  response: RFIAnswer;
};

export type RFIManagerValue = {
  createOfficialResponse: (input: CreateOfficialResponseInput) => Promise<void>;
  createForwardActivity: (
    input: CreateRFIForwardActivityInput
  ) => Promise<void>;
  modifyForwardingList: (
    rfiid: string,
    projectid: string,
    forwardList: OrderedSet<string>
  ) => Promise<void>;
  attachForwardDiscussions: (
    rfi: RFI,
    projectid: string,
    selectedDiscussionIds: OrderedSet<string>
  ) => Promise<void>;
  deleteForwardDiscussion: (
    rfiid: string,
    projectid: string,
    discussionId: string
  ) => Promise<void>;
  refreshFilteredRFIs: () => Promise<void>;
  getRFI: (id: string) => RFI;
  rfis: OrderedMap<string, RFI>;
  reopenRfi: (
    reopenedRfi: NewRFI,
    files: List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<void>;
  getRFIVersionsByBaseId: (baseid: string) => OrderedMap<string, RFI>;
  createRFIDraft: (
    rfi: NewRFI,
    files: List<RFIFile>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<void>;
  updateRFIDraft: (
    rfiid: string,
    newRFI: NewRFI,
    files: List<RFIFile>,
    attachments: List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<void>;
  publishRFIDraft: (
    rfiid: string,
    rfi: NewRFI,
    files: List<RFIFile>,
    removedAttachments: List<RFIAttachment>,
    projectDriveItems: List<ProjectDriveSelectedItem>,
    integrationItems: List<IntegrationsSelectedItem>,
    studioItems: List<StudioProjectFileData>,
    studioData: StudioData
  ) => Promise<void>;
  deleteRFIDraft: (
    rfiid: string,
    projectid: string,
    previousversionid: string
  ) => Promise<void>;
};

const RFIManagerContext = React.createContext<RFIManagerValue | null>(null);

export const RFIManagerProvider = RFIManagerContext.Provider;

export const AppRFIManagerProvider: React.FC = ({ children }) => {
  const { actions: appActions, getState } = React.useContext(AppStoreContext);

  const rfiManager: RFIManagerValue = {
    createOfficialResponse: async (input) => {
      await appActions.createRFIAnswer(
        input.rfi,
        input.response,
        input.fileAttachments,
        input.driveAttachments,
        input.integrationAttachments,
        input.studioAttachments,
        input.studioData
      );
    },
    createForwardActivity: async (input) => {
      await appActions.createRFIForwardActivity(
        input.rfi,
        input.projectid,
        input.fileAttachments,
        input.driveAttachments,
        input.integrationAttachments,
        input.studioAttachments,
        input.studioData,
        input.forwardActivity
      );
    },
    modifyForwardingList: async (rfiid, projectid, forwardList) => {
      await appActions.modifyRFIForwardingList(rfiid, projectid, forwardList);
    },
    attachForwardDiscussions: async (rfi, projectid, selectedDiscussionIds) => {
      await appActions.attachRfiForwardDiscussions(
        rfi,
        projectid,
        selectedDiscussionIds
      );
    },
    deleteForwardDiscussion: async (rfiid, projectid, discussionid) => {
      await appActions.deleteRfiForwardDiscussion(
        rfiid,
        projectid,
        discussionid
      );
    },
    refreshFilteredRFIs: async () => {
      await appActions.queryRFIsFromTable();
    },
    getRFI: (id) => getState().getIn(['rfis', id]),
    rfis: getState().rfis,
    reopenRfi: async (
      reopenedRfi,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    ) => {
      await appActions.reopenRFI(
        reopenedRfi,
        files,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData
      );
    },
    getRFIVersionsByBaseId: (baseid) => {
      const rfiVersions = getState()
        .getIn(['rfis'])
        .filter((rfi: RFI) => rfi.id.startsWith(baseid))
        .sortBy((rfi: RFI) => rfi.versionnumber)
        .reverse() as OrderedMap<string, RFI>;

      return rfiVersions;
    },
    createRFIDraft: async (
      rfi,
      files,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    ) => {
      await appActions.createRFIDraft(
        rfi,
        files,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData
      );
    },
    updateRFIDraft: async (
      rfiid,
      rfi,
      files,
      removedAttachments,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    ) => {
      await appActions.updateRFIDraft(
        rfiid as string,
        rfi,
        files,
        removedAttachments,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData
      );
    },
    publishRFIDraft: async (
      rfiid,
      rfi,
      files,
      removedAttachments,
      projectDriveItems,
      integrationItems,
      studioItems,
      studioData
    ) => {
      await appActions.publishRFIDraft(
        rfiid,
        rfi,
        files,
        removedAttachments,
        projectDriveItems,
        integrationItems,
        studioItems,
        studioData
      );
    },
    deleteRFIDraft: async (rfiid, projectid, previousversionid) => {
      await appActions.deleteRFIDraft(rfiid, projectid, previousversionid);
    },
  };

  return <RFIManagerProvider value={rfiManager}>{children}</RFIManagerProvider>;
};

export const useRFIManager = (): RFIManagerValue => {
  const rfiManager = React.useContext(RFIManagerContext);

  if (rfiManager === null) {
    throw new Error('useRFIManager must be used within a RFIManagerProvider');
  }

  return rfiManager;
};
